import React from 'react'
import { api } from '../api/client'
import type { AppNotification } from '../api/client'
import { useApp } from '../context/AppContext'
import { useDialog } from '../hooks/useDialog'
import { BackLink, Icon, IconButton } from '../ui/Icon'
import { Loading } from '../ui/States'
import { noticeWhen } from './when'
import { openTarget } from './openTarget'
import '../components/Sheet.css'
import './notifications.css'

interface Props {
  onClose: () => void
  // The list is on screen and the API has been told: the bell can clear its badge.
  onSeen: () => void
}

// NotificationsSheet is her list, newest first. It loads the list, then tells
// the API she has seen it (and the bell clears its badge); the dots on new
// items stay for as long as the sheet is open. Tapping an item closes the
// sheet and opens what it is about; an update from Harmovi opens its full
// text here instead; an item with nothing to open is shown but not tappable.
export default function NotificationsSheet({ onClose, onSeen }: Props) {
  const { dispatch } = useApp()
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose)

  const [items, setItems] = React.useState<AppNotification[] | null>(null)
  const [failed, setFailed] = React.useState(false)
  const [reading, setReading] = React.useState<AppNotification | null>(null)
  const opener = React.useRef<HTMLButtonElement | null>(null)
  const headingRef = React.useRef<HTMLHeadingElement>(null)

  const onSeenRef = React.useRef(onSeen)
  onSeenRef.current = onSeen

  // Only the latest load may answer, and none after the sheet is gone: a list
  // she never saw is not marked seen.
  const latest = React.useRef(0)
  const load = React.useCallback(() => {
    const mine = ++latest.current
    setItems(null)
    setFailed(false)
    api
      .listNotifications()
      .then((list) => {
        if (mine !== latest.current) return
        setItems(list)
        api
          .markNotificationsSeen()
          .then(() => onSeenRef.current())
          .catch(() => {})
      })
      .catch(() => {
        if (mine === latest.current) setFailed(true)
      })
  }, [])

  React.useEffect(() => {
    load()
    return () => {
      latest.current++
    }
  }, [load])

  // The update view swaps in for the list, which stays mounted underneath
  // (hidden) so she comes back to the same place; focus follows her.
  React.useEffect(() => {
    if (reading) headingRef.current?.focus({ preventScroll: true })
    else if (opener.current?.isConnected) opener.current.focus({ preventScroll: true })
  }, [reading])

  const open = (n: AppNotification, button: HTMLButtonElement) => {
    if (!n.target) return
    if (n.target.update) {
      opener.current = button
      setReading(n)
      return
    }
    openTarget(n.target, dispatch)
    onClose()
  }

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="sheet-content sheet-content--closable"
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <IconButton icon="close" label="Close" className="sheet-close" onClick={onClose} />

        {reading && (
          <div className="notice-update">
            <BackLink onClick={() => setReading(null)}>Back</BackLink>
            <div className="sheet-header sheet-header--closable">
              <small>{reading.title}</small>
              <h2 ref={headingRef} tabIndex={-1}>{reading.detail}</h2>
            </div>
            {reading.body && (
              <div className="sheet-body">
                <p className="notice-body">{reading.body}</p>
              </div>
            )}
          </div>
        )}

        <div hidden={reading !== null}>
          <div className="sheet-header sheet-header--closable">
            <h2>Notifications</h2>
          </div>
          <div className="sheet-body">
            {failed ? (
              <div className="notice-status" role="alert">
                <p>Couldn’t load your notifications.</p>
                <button type="button" className="btn btn-secondary btn-compact" onClick={load}>Try again</button>
              </div>
            ) : items === null ? (
              <Loading />
            ) : items.length === 0 ? (
              <p className="notice-empty">Nothing yet. Bookings, payments, reviews and forms from your clients show up here.</p>
            ) : (
              <ul className="notice-list">
                {items.map((n, i) => (
                  <li key={i}>
                    {n.target ? (
                      <button type="button" className="notice-item" onClick={(e) => open(n, e.currentTarget)}>
                        <Row n={n} />
                        <Icon name="chevron-right" size={16} />
                      </button>
                    ) : (
                      <div className="notice-item">
                        <Row n={n} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({ n }: { n: AppNotification }) {
  return (
    <>
      {n.new && (
        <>
          <span className="notice-dot" aria-hidden="true" />
          <span className="visually-hidden">New</span>
        </>
      )}
      <span className="notice-text">
        <span className="notice-title">{n.title}</span>
        <span className="notice-detail">{n.detail}</span>
        <span className="notice-when">{noticeWhen(n.at)}</span>
      </span>
    </>
  )
}
